import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await api.post('/contact', formData);
      toast.success(res.data.message || 'Thank you! We will get back to you within 24-48 hours.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="container-app py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Contact <span className="text-amber-400">Us</span>
            </h1>
            <p className="mt-4 text-slate-300 text-lg">
              Have questions? We're here to help. Reach out to our support team.
            </p>
          </motion.div>
        </div>
      </div>
      
      <div className="container-app py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            {/* Office Details */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl p-6 border border-slate-200"
            >
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Head Office
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-600 mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">LegalHub</p>
                    <p className="text-sm text-slate-600">
                      Mumbai, Maharashtra<br />
                      India
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Contact Methods */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 border border-slate-200"
            >
              <h2 className="text-xl font-bold text-slate-900 mb-4">Get in Touch</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <Phone className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <a href="tel:+919076433479" className="font-medium text-slate-900 hover:text-blue-600">+91 9076433479</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                  <Mail className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-xs text-slate-500">General Enquiries</p>
                    <a href="mailto:info@legalhubs.info" className="font-medium text-slate-900 hover:text-amber-600 block">info@legalhubs.info</a>
                    <a href="mailto:deepakrao1983@gmail.com" className="font-medium text-slate-900 hover:text-amber-600 block">deepakrao1983@gmail.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <Mail className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-xs text-slate-500">Lawyer Registration</p>
                    <a href="mailto:info@legalhubs.info" className="font-medium text-slate-900 hover:text-emerald-600 block">info@legalhubs.info</a>
                    <a href="mailto:deepakrao1983@gmail.com" className="font-medium text-slate-900 hover:text-emerald-600 block">deepakrao1983@gmail.com</a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Business Hours */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 border border-slate-200"
            >
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Business Hours
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Monday - Friday</span>
                  <span className="font-medium text-slate-900">9:00 AM - 7:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Saturday</span>
                  <span className="font-medium text-slate-900">10:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Sunday</span>
                  <span className="font-medium text-slate-900">Closed</span>
                </div>
                <p className="text-xs text-slate-400 mt-4 pt-4 border-t">
                  * All times are in Indian Standard Time (IST)
                </p>
              </div>
            </motion.div>
          </div>
          
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl p-8 border border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-blue-600" />
                Send us a Message
              </h2>
              <p className="text-slate-500 mb-6">
                Fill out the form below and we'll get back to you within 24-48 hours.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Subject *</label>
                    <Select
                      value={formData.subject}
                      onValueChange={(value) => setFormData({ ...formData, subject: value })}
                    >
                      <SelectTrigger data-testid="contact-subject">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Enquiry</SelectItem>
                        <SelectItem value="booking">Booking Issue</SelectItem>
                        <SelectItem value="payment">Payment/Refund</SelectItem>
                        <SelectItem value="lawyer">Lawyer Registration</SelectItem>
                        <SelectItem value="complaint">Complaint</SelectItem>
                        <SelectItem value="partnership">Business Partnership</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please describe your query in detail..."
                    rows={5}
                    required
                    data-testid="contact-message"
                  />
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
                  <p>
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a> and{' '}
                    <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>.
                  </p>
                </div>
                
                <Button
                  type="submit"
                  className="w-full md:w-auto px-8 py-6 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </span>
                  )}
                </Button>
              </form>
            </div>
            
            {/* Grievance Officer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mt-6"
            >
              <h3 className="font-bold text-amber-800 mb-3">Grievance Redressal Officer</h3>
              <p className="text-sm text-amber-700 mb-3">
                As per the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021:
              </p>
              <div className="bg-white rounded-lg p-4 text-sm">
                <p><strong>Email:</strong> info@legalhubs.info</p>
                <p><strong>Phone:</strong> +91 9076433479</p>
                <p className="text-slate-500 mt-2">
                  Complaints will be acknowledged within 24 hours and resolved within 15 days.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
